import React, { useEffect, useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogTitle, TextField } from '@material-ui/core';
import { TreeItem, TreeView } from '@material-ui/lab';
import { DocumentTemplateType } from '../../api-client/autogenerated';
import { AddBoxOutlined, IndeterminateCheckBoxOutlined, Search } from '@material-ui/icons';
import DocumentTypeTreeItem from './DocumentTypeTreeItem';
import DocumentTreeItem from './DocumentTreeItem';
import CircularLoader from '../loader/CircularLoader';
import Button from '@material-ui/core/Button';
import Typography from '@material-ui/core/Typography';
import InputAdornment from '@material-ui/core/InputAdornment';
import { makeStyles } from '@material-ui/core/styles';
import FormControl from '@material-ui/core/FormControl';
import { DOCUMENT_PHASES, DocumentFileTree, generateUniqueId } from '../../scripts/utils';
import { useDispatch, useSelector } from 'react-redux';
import { reloadDocument } from '../../features/document/actions';
import { modifyDocumentById } from '../../models/api/documents';
import { getDocumentState } from '../../features/document/selectors';
import { addSnackbar } from '../../features/snackbar/actions';

type Props = {
  documents: DocumentFileTree[];
  setDocuments: (documents: DocumentFileTree[]) => void;
  open: boolean;
  handleClose: () => void;
  onComplete?: () => void;
  documentId?: string;
  title?: string;
  subtitle?: string;
  /**
   * Checking a document clears whatever else was checked, for callers that take one document
   * rather than a set -- the Quality Control Form's file-path fields.
   */
  singleSelect?: boolean;
  /**
   * Groups the document types under the project's phases. Every phase is listed, whether or not
   * the project has anything in it, so the reader can see what a phase would hold; an empty one
   * is greyed out and does not open.
   *
   * Storytelling is only listed when `showStorytellingPhase` is set, which the caller ties to the
   * subscriber having the module.
   */
  groupByPhase?: boolean;
  showStorytellingPhase?: boolean;
};

const useStyles = makeStyles(() => ({
  textfield: {
    height: '32px',

    /* Gray / Gray 50 */
    background: '#F9F9F9',

    /* Gray / Gray 400 Brand dark */
    border: '1px solid #949494',
    'border-radius': '5px',
  },
  icon: {
    fill: '#B2B1B2',
    marginLeft: '4px',
  },
  phaseLabel: {
    fontWeight: 500,
    backgroundColor: 'transparent',
    '&:hover': { backgroundColor: 'transparent' },
  },
  // A phase the project has nothing in: still listed, but plainly not something to open.
  emptyPhaseLabel: {
    fontWeight: 500,
    color: '#949494', // Gray 400
    backgroundColor: 'transparent',
    cursor: 'default',
    '&:hover': { backgroundColor: 'transparent' },
  },
  phaseGroup: {
    marginTop: 4,
  },
}));

export default function AssociateDocumentDialog(props: Props) {
  const classes = useStyles();
  const dispatch = useDispatch();
  const {
    open,
    handleClose,
    documents,
    setDocuments,
    documentId,
    onComplete,
    title,
    subtitle,
    singleSelect,
    groupByPhase,
    showStorytellingPhase,
  } = props;

  const document = useSelector(getDocumentState);

  const [isLoading, setIsLoading] = useState(true);
  const [searchInput, setSearchInput] = useState('');
  const [documentsToAssociate, setDocumentsToAssociate] = useState<string[]>([]);

  useEffect(() => {
    setDocumentsToAssociate(
      documents
        .map((tree) => tree.documents.filter((doc) => doc.checked).map((doc) => doc.id))
        .flat(),
    );
  }, [documents]);

  const handleCheck = (
    event: React.ChangeEvent<HTMLInputElement>,
    checked: boolean,
    type: DocumentTemplateType,
  ) => {
    const newDocuments = [...documents];
    newDocuments
      .find((tree) => tree.type === type)!
      .documents.forEach((doc) => (doc.checked = checked));
    setDocuments(newDocuments);
  };

  const handleSubCheck = (checked: boolean, type: string, id: string) => {
    const newDocuments = [...documents];
    if (singleSelect && checked) {
      newDocuments.forEach((tree) => tree.documents.forEach((doc) => (doc.checked = false)));
    }
    newDocuments
      .find((tree) => tree.type === type)!
      .documents.find((doc) => doc.id === id)!.checked = checked;
    setDocuments(newDocuments);
  };

  const handleDialogClose = async () => {
    if (documentsToAssociate.length > 0 && documentId) {
      await modifyDocumentById(documentId, {
        associatedDocuments: { addIds: documentsToAssociate },
      });

      if (document) {
        dispatch(reloadDocument());
        dispatch(
          addSnackbar({
            id: Date.now(),
            message: 'Successfully added new associations',
            severity: 'success',
          }),
        );
      }
    }
    onComplete?.();
    handleClose();
  };

  useEffect(() => {
    if (documents.length > 0) setIsLoading(false);
    else setIsLoading(true);
  }, [documents]);

  /** The trees the search leaves, with the types the project has nothing in dropped. */
  const visibleTrees = useMemo(
    () =>
      documents
        .map(({ type, documents: typeDocuments }) => ({
          type,
          documents: searchInput
            ? typeDocuments.filter((doc) => doc.title.includes(searchInput))
            : typeDocuments,
        }))
        .filter((tree) => tree.documents.length > 0),
    [documents, searchInput],
  );

  const phases = useMemo(
    () =>
      DOCUMENT_PHASES.filter(
        (phase) => phase.title !== 'Storytelling' || showStorytellingPhase,
      ).map((phase) => ({
        title: phase.title,
        trees: visibleTrees.filter((tree) => phase.types.includes(tree.type)),
      })),
    [visibleTrees, showStorytellingPhase],
  );

  return (
    <Dialog
      open={open}
      fullWidth
      PaperProps={{ style: { maxWidth: '720px' } }}
      onClose={handleClose}
    >
      <DialogTitle
        disableTypography
        style={{ display: 'flex', alignItems: 'center', paddingBottom: 8 }}
      >
        <Typography variant="h1" style={{ textTransform: 'none', lineHeight: 1 }}>
          {title || 'Tag Existing Documents'}
        </Typography>
        <div style={{ display: 'inline-flex', flexGrow: 100 }} />
        <Button
          id="document-done-btn"
          color="primary"
          variant="contained"
          onClick={handleDialogClose}
        >
          Done
        </Button>
      </DialogTitle>
      <DialogContent>
        <Typography style={{ marginBottom: 16 }}>
          {subtitle ||
            'CENTERLINE allows you to tag new documents with existing documents on the project ' +
              "for cross-referencing. Tagging an existing document automatically adds that document's " +
              'users to the notification list of the new document.'}
        </Typography>
        <FormControl variant="outlined">
          <TextField
            className={classes.textfield}
            placeholder="Search..."
            value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search
                    fontSize="small"
                    classes={{
                      root: classes.icon,
                    }}
                  />
                </InputAdornment>
              ),
            }}
            style={{ marginBottom: 12 }}
          />
        </FormControl>
        {isLoading ? (
          <CircularLoader />
        ) : (
          <TreeView
            defaultCollapseIcon={<IndeterminateCheckBoxOutlined />}
            defaultExpandIcon={<AddBoxOutlined />}
          >
            {groupByPhase
              ? phases.map(({ title, trees }) => {
                  if (trees.length === 0) {
                    // Nothing in this phase: shown so the reader can see the phase exists, greyed
                    // out and with no expand arrow, since there is nothing to open.
                    return (
                      <TreeItem
                        key={title}
                        nodeId={`phase:${title}`}
                        label={`${title} (no documents)`}
                        classes={{ label: classes.emptyPhaseLabel }}
                        onClick={(event) => event.preventDefault()}
                      />
                    );
                  }
                  return (
                    <TreeItem
                      key={title}
                      nodeId={`phase:${title}`}
                      label={title}
                      classes={{ label: classes.phaseLabel, group: classes.phaseGroup }}
                    >
                      {trees.map((tree) =>
                        // A phase whose only grouping is itself -- Storytelling, whose forms have
                        // no document type to group under -- lists its entries directly rather
                        // than under a second heading repeating the phase's own name.
                        tree.type === title ? (
                          tree.documents.map((document) => (
                            <DocumentTreeItem
                              key={document.id}
                              type={tree.type}
                              document={document}
                              handleCheck={handleSubCheck}
                            />
                          ))
                        ) : (
                          <DocumentTypeTreeItem
                            key={`${title}:${tree.type}`}
                            tree={tree}
                            handleCheck={handleCheck}
                            handleSubCheck={handleSubCheck}
                          />
                        ),
                      )}
                    </TreeItem>
                  );
                })
              : visibleTrees.map((doc) => {
                  return (
                    <DocumentTypeTreeItem
                      key={generateUniqueId()}
                      tree={doc}
                      handleCheck={handleCheck}
                      handleSubCheck={handleSubCheck}
                    />
                  );
                })}
          </TreeView>
        )}
      </DialogContent>
    </Dialog>
  );
}
